import { Hammer } from "lucide-react";
import ServicePage from "./ServicePage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import { useServicePhotos } from "../hooks/useServicePhotos";

const basePhotos = [
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/z0dfbfme_IMG_0007.jpeg", alt: "Funderingswerk wapening - Aannemer Breukelen" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/kx9l7t85_IMG_0504.jpeg", alt: "Betonvloer storten - Bouwproject Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/sz5cfhqw_IMG_0009.jpeg", alt: "Houtskeletbouw renovatie - Cardoso Bouw" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/gbul91vb_IMG_0022.jpeg", alt: "Renovatie interieur met houten balken - Aannemer Utrecht" },
  { src: "https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/t3bzg8ue_IMG_0034.jpeg", alt: "Afgewerkte renovatie met eiken vloer - Cardoso" },
];

const faqs = [
  {
    q: "Wat doet een aannemer precies?",
    a: "Een aannemer coördineert en voert bouw- en renovatiewerkzaamheden uit. Bij Cardoso Bouw en Onderhoud heeft u één aanspreekpunt voor planning, uitvoering en afwerking van A tot Z, inclusief de afstemming met onze vaste partners voor elektra en installaties.",
  },
  {
    q: "Hoe verloopt een offerte aanvraag?",
    a: "Na uw aanvraag nemen wij binnen 24 uur contact met u op. Wij komen vrijblijvend bij u langs om de situatie op te nemen en uw wensen te bespreken. Daarna ontvangt u een gedetailleerde offerte met een duidelijke planning. De offerte is altijd gratis en vrijblijvend.",
  },
  {
    q: "In welke regio werkt Cardoso Bouw als aannemer?",
    a: "Vanuit Breukelen werken wij in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland, waaronder Amsterdam, Utrecht, Rotterdam, Den Haag, Hilversum, Amersfoort, Almere, Haarlem en Leiden.",
  },
  {
    q: "Hoe lang duurt een renovatie of verbouwing?",
    a: "Dat hangt af van de omvang. Een kleine verbouwing kan binnen enkele weken klaar zijn, een complete woningrenovatie duurt doorgaans enkele maanden. U ontvangt vooraf altijd een realistische planning en wij houden u tijdens het project op de hoogte.",
  },
  {
    q: "Regelen jullie ook vergunningen en constructieberekeningen?",
    a: "Ja. Wij adviseren over de benodigde vergunningen en werken samen met constructeurs en vaste partners voor berekeningen, elektra en installaties, zodat u zich nergens zorgen over hoeft te maken.",
  },
  {
    q: "Zijn jullie verzekerd en werken jullie met een contract?",
    a: "Ja. Wij zijn ingeschreven bij de KVK, verzekerd voor onze werkzaamheden en leggen alle afspraken vast in een duidelijke offerte en opdrachtbevestiging, zodat u vooraf precies weet wat u kunt verwachten.",
  },
];

function AannemerCarousel() {
  const projectPhotos = useServicePhotos("aannemer", basePhotos);
  
  return (
    <div className="px-12" data-testid="aannemer-carousel">
      <Carousel opts={{ loop: true }} className="w-full">
        <CarouselContent>
          {projectPhotos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
        <CarouselNext className="bg-[#005F5F] text-white border-none hover:bg-[#C5A059] hover:text-white" />
      </Carousel>
    </div>
  );
}

export default function AannemerPage() {
  return (
    <ServicePage
      seoTitle="Aannemer Breukelen | Professionele Bouw & Renovatie - Cardoso"
      seoDescription="Professionele aannemer in Breukelen voor complete bouwprojecten en renovaties. 8+ jaar ervaring, 9 vakmannen. Vraag een vrijblijvende offerte aan."
      seoKeywords="aannemer Breukelen, bouwbedrijf Breukelen, renovatie Breukelen, woning renovatie, verbouwing Breukelen, aannemer Utrecht, aannemer Hilversum, aannemer Loosdrecht, aannemer Noord-Holland, aannemer Zuid-Holland, aannemer Flevoland"
      title="Aannemer"
      subtitle="Aannemer Breukelen"
      heroImage="https://customer-assets.emergentagent.com/job_cardoso-bouw/artifacts/gbul91vb_IMG_0022.jpeg"
      icon={Hammer}
      description="Op zoek naar een betrouwbare aannemer in Breukelen? Cardoso Bouw en Onderhoud is uw professionele partner voor complete bouwprojecten en renovaties. Met meer dan 8 jaar ervaring en een team van 9 vakmannen staan wij garant voor kwaliteit, betrouwbaarheid en een professionele afwerking. Wij verzorgen alle werkzaamheden van A tot Z, zodat u een vast aanspreekpunt heeft gedurende het hele traject."
      features={[
        "Complete woningrenovaties",
        "Aanbouwen en uitbouwen",
        "Verbouwingen op maat",
        "Projectmanagement en coordinatie",
        "Funderingswerk en constructie",
        "Dakwerk en dakrenovatie",
        "Gevelbekleding en isolatie",
        "Nieuwbouw en seriematige bouw",
      ]}
      faqs={faqs}
      extraContent={
        <div className="space-y-8">
          {/* PROJECT CAROUSEL */}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A] mb-6">
              Onze projecten
            </h2>
            <AannemerCarousel />
          </div>

          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0F172A]">
            Uw betrouwbare aannemer in Breukelen
          </h2>
          <p className="text-slate-600 text-base leading-relaxed">
            Bij Cardoso Bouw en Onderhoud begrijpen wij dat een bouw- of renovatieproject een grote 
            investering is. Daarom nemen wij de tijd om uw wensen goed in kaart te brengen en een 
            gedetailleerde offerte op te stellen. Tijdens het project houden wij u continu op de 
            hoogte van de voortgang en zorgen wij ervoor dat alles volgens planning verloopt.
          </p>
          <p className="text-slate-600 text-base leading-relaxed">
            Als ervaren aannemer in de regio Utrecht en Noord-Holland hebben wij inmiddels honderden projecten 
            succesvol opgeleverd. Van kleine verbouwingen tot complete woningrenovaties, geen project 
            is ons te groot of te klein. Wij werken uitsluitend met hoogwaardige materialen en zorgen 
            voor een professionele afwerking waar u jarenlang plezier van heeft.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Aannemer voor de gehele regio
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Vanuit onze vestiging in Breukelen bedienen wij klanten in heel Noord-Holland, Zuid-Holland, Utrecht en Flevoland. Wij komen graag bij u langs voor een vrijblijvende offerte. 
            Dit betekent dat wij werkzaam zijn in onder andere Utrecht, Amsterdam, Hilversum, Maarssen, 
            Amersfoort, Almere, Loosdrecht, Naarden, Bussum en Weesp. Of u nu een woning wilt renoveren, 
            een aanbouw wilt realiseren of een complete verbouwing plant, wij staan voor u klaar.
          </p>

          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0F172A]">
            Werkwijze
          </h3>
          <p className="text-slate-600 text-base leading-relaxed">
            Ons traject begint met een vrijblijvend kennismakingsgesprek bij u thuis of op locatie. 
            Wij nemen de situatie op, bespreken uw wensen en mogelijkheden en stellen vervolgens 
            een gedetailleerde offerte op. Na akkoord plannen wij de werkzaamheden in en starten 
            wij op de afgesproken datum.
          </p>
          <p className="text-slate-600 text-base leading-relaxed">
            Tijdens de bouw of renovatie bent u altijd op de hoogte. Wij communiceren helder en 
            direct over de voortgang, eventuele wijzigingen en de planning. Na oplevering doen 
            wij samen een eindcontrole en zorgen wij dat alles naar uw volle tevredenheid is.
          </p>
          <p className="text-slate-600 text-base leading-relaxed">
            Onze klanten waarderen ons met gemiddeld 5 sterren op Google. Dat is waar wij trots 
            op zijn en wat ons motiveert om elke dag het beste uit onszelf te halen.
          </p>
        </div>
      }
    />
  );
}
